import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiMoon, FiChevronDown, FiExternalLink, FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import './term.css'; // Your CSS file

// ----------------------------------------------------------------------
// 0. DATA: Legal Content (Keep as is)
// ----------------------------------------------------------------------
const COMPANY_NAME = "NOW NFC";
const WEBSITE_URL = "https://www.nownfc.com";
const PRIVACY_POLICY_URL = `${WEBSITE_URL}/privacy-policy`;
const LEGAL_EMAIL = "legal@nownfc.com";
const COMPANY_ADDRESS = "1st 'A' Floor, Shri Ram Building, Infront of Parihar Dairy, Main Mahamandir Circle, Nagori Gate Kila Rd, Jodhpur, Rajasthan 342006, India";
const JURISDICTION_STATE_COUNTRY = "Jodhpur, Rajasthan, India";
const GOVERNING_LAW_JURISDICTION = "Republic of India";
const ARBITRATION_BODY = "the Indian Council of Arbitration (ICA)";
const ARBITRATION_RULES = "Rules of Arbitration of the ICA";
const ARBITRATION_CITY = "Jodhpur, Rajasthan";

const termsSectionsData = [
 {
   id: "intro",
   title: "1. Introduction and Agreement to Terms",
   content: `These Terms constitute a legally binding agreement made between you, whether personally or on behalf of an entity (“you”) and ${COMPANY_NAME}, concerning your access to and use of our NFC card products and associated digital services (collectively, the "Services"). By accessing or using the Services, you acknowledge that you have read, understood, and agree to be bound by all of these Terms and Conditions. If you do not agree with all of these Terms and Conditions, then you are expressly prohibited from using the Services and you must discontinue use immediately. Supplemental terms and conditions or documents that may be posted on ${WEBSITE_URL} from time to time are hereby expressly incorporated herein by reference. We reserve the right, in our sole discretion, to make changes or modifications to these Terms at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of these Terms, and you waive any right to receive specific notice of each such change. It is your responsibility to periodically review these Terms to stay informed of updates. You will be subject to, and will be deemed to have been made aware of and to have accepted, the changes in any revised Terms by your continued use of the Services after the date such revised Terms are posted. The information provided on the Services is not intended for distribution to or use by any person or entity in any jurisdiction or country where such distribution or use would be contrary to law or regulation or which would subject us to any registration requirement within such jurisdiction or country. Accordingly, those persons who choose to access the Services from other locations do so on their own initiative and are solely responsible for compliance with local laws, if and to the extent local laws are applicable.`,
 },
 {
   id: "services",
   title: "2. Description of Services",
   content: `${COMPANY_NAME} provides innovative NFC-enabled products and digital platforms ("NFC products and digital platforms"). Our Services are designed to facilitate: <ul><li><strong>Digital Business Cards:</strong> Modern, shareable contact information via NFC cards.</li><li><strong>Identity Verification:</strong> Secure and quick identity confirmation using NFC technology for various applications.</li><li><strong>Smart Contactless Check-ins:</strong> Streamlined check-in processes for hospitality, corporate, or event environments.</li><li><strong>Event Entry and Smart Automation Systems:</strong> Efficient access control and integration with smart systems for enhanced user experiences.</li></ul>The Services may include physical NFC cards, a web-based platform, mobile applications, and APIs that allow you to manage your NFC card data, interactions, and associated functionalities. The specifics of the services offered can be found on our website ${WEBSITE_URL} or in the documentation accompanying your specific NFC product. These <strong>NFC Card Service Terms</strong> cover all aspects of such offerings.`,
 },
 {
   id: "user-accounts",
   title: "3. User Accounts and Registration",
   content: `To access certain features of our digital platforms, you may be required to register for an account. When you register for an account, you agree to: <ul><li>Provide accurate, current, and complete information as prompted by any registration forms on the Service ("Registration Data").</li><li>Maintain and promptly update the RegistrationData to keep it accurate, current, and complete.</li><li>Maintain the security of your password and identification.</li><li>Notify us immediately of any unauthorized use of your account or any other breach of security.</li><li>Accept all risks of unauthorized access to the Registration Data and any other information you provide to us.</li><li>You are responsible for all activities that occur under your account.</li></ul>`,
 },
 {
   id: "user-obligations",
   title: "4. User Obligations and Responsibilities",
   content: `As a user of our Services, you agree to: <ul><li>Use the Services in compliance with all applicable local, state, national, and international laws, rules, and regulations.</li><li>Be solely responsible for the accuracy, quality, integrity, legality, reliability, and appropriateness of any data or information you input, upload, or link to your NFC card or our digital platforms.</li><li>Safeguard your NFC card from loss, theft, or unauthorized use. Report any suspected security breach or loss of your card to us immediately.</li><li>Not use the Services for any fraudulent, unlawful, or malicious activities.</li><li>Ensure that any digital content linked to or transmitted via your NFC card (e.g., for digital business cards) is appropriate and does not infringe upon the rights of any third party.</li><li>Cooperate with us in any investigation regarding fraudulent or improper activity.</li></ul>`,
 },
 {
   id: "ip-rights",
   title: "5. Licensing and Intellectual Property Rights",
   content: `<strong>License to Use Services:</strong> Subject to your compliance with these Terms, ${COMPANY_NAME} grants you a limited, non-exclusive, non-transferable, non-sublicensable license to access and use the Services for your personal or internal business purposes, as intended through the provided functionality of the Services and as permitted under these Terms.<br><br><strong>Our Intellectual Property:</strong> The Services, including all underlying technology, software, designs, graphics, text, user interfaces, visual interfaces, photographs, trademarks, logos, sounds, music, artwork, computer code, and other content (collectively, "Our Content"), including but not limited to the design, structure, selection, coordination, expression, "look and feel," and arrangement of Our Content, contained on the Services is owned, controlled, or licensed by or to ${COMPANY_NAME}, and is protected by copyright, patent, trademark laws, and various other intellectual property rights and unfair competition laws.<br><br>Except as expressly provided in these Terms, no part of the Services and no Our Content may be copied, reproduced, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, or distributed in any way (including "mirroring") to any other computer, server, website, or other medium for publication or distribution or for any commercial enterprise, without ${COMPANY_NAME}'s express prior written consent.`,
 },
 {
   id: "prohibited-actions",
   title: "6. Usage Limitations and Prohibited Actions",
   content: `You agree not to, and will not assist, encourage, or enable others to use the Services to: <ul><li>Engage in any activity that is illegal, fraudulent, deceptive, or harmful.</li><li><strong>Reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code, object code, or underlying structure, ideas, or algorithms of the NFC technology embedded in our cards or any software, documentation, or data related to the Services.</strong></li><li>Modify, translate, or create derivative works based on the Services or any part thereof.</li><li>Rent, lease, distribute, sell, resell, assign, or otherwise transfer your rights to use the Services.</li><li><strong>Misuse digital identity features by impersonating any person or entity, or falsely stating or otherwise misrepresenting your affiliation with a person or entity.</strong></li><li>Use the Services to transmit any material that contains software viruses or any other computer code, files, or programs designed to interrupt, destroy, or limit the functionality of any computer software or hardware or telecommunications equipment.</li><li>Interfere with or disrupt the integrity or performance of the Services or the data contained therein.</li><li>Attempt to gain unauthorized access to the Services or their related systems or networks.</li><li>Use any automated system, including without limitation "robots," "spiders," "offline readers," etc., to access the Services in a manner that sends more request messages to our servers than a human can reasonably produce in the same period by using a conventional on-line web browser.</li><li>Use the Services for any purpose for which they are not designed or intended. This <strong>Smart Card Usage Policy</strong> is critical for maintaining the integrity of our offerings.</li></ul>`,
 },
 {
   id: "payment-billing",
   title: "7. Payment, Billing, and Refunds (If Applicable)",
   content: `<strong>Fees:</strong> Certain Services or features may be subject to fees. You agree to pay all applicable fees as described on ${WEBSITE_URL} or in a separate agreement in connection with such Services selected by you.<br><br><strong>Billing:</strong> We may use a third-party payment processor to bill you through a payment account linked to your account on the Services. The processing of payments will be subject to the terms, conditions, and privacy policies of the payment processor in addition to these Terms.<br><br><strong>Subscription Services:</strong> If you subscribe to any recurring fee-based Services, your subscription will automatically renew for the same subscription period unless you cancel your subscription before the end of the current subscription period. You can cancel your subscription through your account settings or by contacting us.<br><br><strong>Refunds:</strong> All fees are non-refundable except as required by law or as explicitly stated in our Refund Policy (if any), which will be made available on ${WEBSITE_URL}. Please review our <strong>NFC Card Terms</strong> regarding product returns and service cancellations.<br><br><strong>Taxes:</strong> All fees are exclusive of applicable taxes, levies, or duties imposed by taxing authorities, and you shall be responsible for payment of all such taxes, levies, or duties.`,
 },
 {
   id: "data-privacy",
   title: "8. Data Privacy and Security",
   content: `Your privacy is important to us. Our Privacy Policy, available at <a href="${PRIVACY_POLICY_URL}" target="_blank" rel="noopener noreferrer">${PRIVACY_POLICY_URL} <FiExternalLink style={{verticalAlign: 'middle', marginLeft: '2px'}} size="1em"/></a>, explains how we collect, use, and share your personal information in connection with the Services. By using the Services, you agree to the collection, use, and sharing of your information as described in our Privacy Policy. These <strong>NFC Data Terms and Conditions</strong> are integral to your use of our services. We implement reasonable security measures to protect your information, but we cannot guarantee absolute security.`,
 },
 {
   id: "third-party",
   title: "9. Third-Party Services and Links",
   content: `The Services may contain links to third-party websites, applications, or services that are not owned or controlled by ${COMPANY_NAME}. We do not endorse or assume any responsibility for any such third-party sites, information, materials, products, or services. If you access a third-party website or service from the Services, you do so at your own risk, and you understand that these Terms and our Privacy Policy do not apply to your use of such sites. You expressly relieve ${COMPANY_NAME} from any and all liability arising from your use of any third-party website, service, or content.`,
 },
 {
   id: "limitation-liability",
   title: "10. Limitation of Liability",
   content: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ${COMPANY_NAME}, ITS AFFILIATES, AGENTS, DIRECTORS, EMPLOYEES, SUPPLIERS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO THE USE OF, OR INABILITY TO USE, THE SERVICES.<br><br>UNDER NO CIRCUMSTANCES WILL ${COMPANY_NAME} BE RESPONSIBLE FOR ANY DAMAGE, LOSS, OR INJURY RESULTING FROM HACKING, TAMPERING, OR OTHER UNAUTHORIZED ACCESS OR USE OF THE SERVICES OR YOUR ACCOUNT OR THE INFORMATION CONTAINED THEREIN.<br><br>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ${COMPANY_NAME} ASSUMES NO LIABILITY OR RESPONSIBILITY FOR ANY (I) ERRORS, MISTAKES, OR INACCURACIES OF CONTENT; (II) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER, RESULTING FROM YOUR ACCESS TO OR USE OF OUR SERVICES; (III) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS AND/OR ANY AND ALL PERSONAL INFORMATION STORED THEREIN; (IV) ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE SERVICES; (V) ANY BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE THAT MAY BE TRANSMITTED TO OR THROUGH OUR SERVICES BY ANY THIRD PARTY; (VI) ANY ERRORS OR OMISSIONS IN ANY CONTENT OR FOR ANY LOSS OR DAMAGE INCURRED AS A RESULT OF THE USE OF ANY CONTENT POSTED, EMAILED, TRANSMITTED, OR OTHERWISE MADE AVAILABLE THROUGH THE SERVICES; AND/OR (VII) USER CONTENT OR THE DEFAMATORY, OFFENSIVE, OR ILLEGAL CONDUCT OF ANY THIRD PARTY.<br><br>IN NO EVENT SHALL ${COMPANY_NAME}, ITS AFFILIATES, AGENTS, DIRECTORS, EMPLOYEES, SUPPLIERS, OR LICENSORS BE LIABLE TO YOU FOR ANY CLAIMS, PROCEEDINGS, LIABILITIES, OBLIGATIONS, DAMAGES, LOSSES OR COSTS IN AN AMOUNT EXCEEDING THE AMOUNT YOU PAID TO ${COMPANY_NAME} HEREUNDER IN THE SIX (6) MONTHS PRIOR TO THE EVENT GIVING RISE TO THE LIABILITY, OR ONE HUNDRED U.S. DOLLARS ($100.00), WHICHEVER IS GREATER.<br><br>THIS LIMITATION OF LIABILITY SECTION APPLIES WHETHER THE ALLEGED LIABILITY IS BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR ANY OTHER BASIS, EVEN IF ${COMPANY_NAME} HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.`,
 },
 {
   id: "disclaimers-warranty",
   title: "11. Disclaimers of Warranty",
   content: `THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. USE OF THE SERVICES IS AT YOUR OWN RISK. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICES ARE PROVIDED WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.<br><br>NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM ${COMPANY_NAME} OR THROUGH THE SERVICES WILL CREATE ANY WARRANTY NOT EXPRESSLY STATED HEREIN. WITHOUT LIMITING THE FOREGOING, ${COMPANY_NAME}, ITS SUBSIDIARIES, ITS AFFILIATES, AND ITS LICENSORS DO NOT WARRANT THAT THE CONTENT IS ACCURATE, RELIABLE, OR CORRECT; THAT THE SERVICES WILL MEET YOUR REQUIREMENTS; THAT THE SERVICES WILL BE AVAILABLE AT ANY PARTICULAR TIME OR LOCATION, UNINTERRUPTED OR SECURE; THAT ANY DEFECTS OR ERRORS WILL BE CORRECTED; OR THAT THE SERVICES ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. ANY CONTENT DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE USE OF THE SERVICES IS DOWNLOADED AT YOUR OWN RISK AND YOU WILL BE SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR COMPUTER SYSTEM OR MOBILE DEVICE OR LOSS OF DATA THAT RESULTS FROM SUCH DOWNLOAD OR YOUR USE OF THE SERVICES.<br><br>${COMPANY_NAME} DOES NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR ANY PRODUCT OR SERVICE ADVERTISED OR OFFERED BY A THIRD PARTY THROUGH THE SERVICES OR ANY HYPERLINKED WEBSITE OR SERVICE, AND ${COMPANY_NAME} WILL NOT BE A PARTY TO OR IN ANY WAY MONITOR ANY TRANSACTION BETWEEN YOU AND THIRD-PARTY PROVIDERS OF PRODUCTS OR SERVICES.`,
 },
 {
   id: "indemnification",
   title: "12. Indemnification",
   content: `You agree to defend, indemnify, and hold harmless ${COMPANY_NAME} and its subsidiaries, agents, licensors, managers, and other affiliated companies, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees) arising from: (i) your use of and access to the Services, including any data or content transmitted or received by you; (ii) your violation of any term of these Terms, including without limitation your breach of any of the representations and warranties above; (iii) your violation of any third-party right, including without limitation any right of privacy or Intellectual Property Rights; (iv) your violation of any applicable law, rule, or regulation; (v) any content that is submitted via your account including without limitation misleading, false, or inaccurate information; (vi) your willful misconduct; or (vii) any other party’s access and use of the Services with your unique username, password, or other appropriate security code.`,
 },
 {
   id: "termination",
   title: "13. Termination of Services Clause",
   content: `<strong>Termination by You:</strong> You may terminate your account and stop using the Services at any time by contacting us at ${LEGAL_EMAIL} or through your account settings, if available.<br><br><strong>Termination by Us:</strong> We may, in our sole discretion, suspend or terminate your access to all or part of the Services, with or without notice, for any reason, including, without limitation, if we believe that you have violated or acted inconsistently with the letter or spirit of these Terms. We reserve the right to modify or discontinue, temporarily or permanently, the Services (or any part thereof) with or without notice.<br><br><strong>Effect of Termination:</strong> Upon termination, your right to use the Services will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.`,
 },
 {
   id: "governing-law",
   title: "14. Governing Law and Jurisdiction",
   content: `These Terms shall be governed and construed in accordance with the laws of ${GOVERNING_LAW_JURISDICTION}, without regard to its conflict of law provisions.<br><br>You agree that any legal action or proceeding arising out of or relating to these Terms or the Services shall be brought exclusively in the competent courts located in ${JURISDICTION_STATE_COUNTRY}. You hereby consent to the exclusive jurisdiction and venue of such courts.`,
 },
 {
   id: "dispute-resolution",
   title: "15. Dispute Resolution",
   content: `<strong>Negotiation:</strong> In the event of any dispute, claim, question, or disagreement arising from or relating to these Terms or the breach thereof, the parties hereto shall use their best efforts to settle the dispute, claim, question, or disagreement. To this effect, they shall consult and negotiate with each other in good faith and, recognizing their mutual interests, attempt to reach a just and equitable solution satisfactory to both parties.<br><br><strong>Binding Arbitration:</strong> If the parties do not reach such a solution within a period of sixty (60) days, then upon notice by either party to the other, all disputes, claims, questions, or differences shall be finally settled by binding arbitration administered by ${ARBITRATION_BODY} in accordance with its ${ARBITRATION_RULES}. The arbitration shall take place in ${ARBITRATION_CITY}, and the language of arbitration shall be English. The arbitrator's award shall be final and binding, and judgment may be entered upon it in any court having jurisdiction thereof.`,
 },
 {
   id: "modification-terms",
   title: "16. Modification of Terms",
   content: `We reserve the right, at our sole discretion, to modify or replace these <strong>NFC Card Terms</strong> at any time. If a revision is material, we will provide at least thirty (30) days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Services after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Services. We encourage you to periodically review this <strong>${COMPANY_NAME} User Agreement</strong> to stay informed of updates.`,
 },
 {
   id: "severability",
   title: "17. Severability",
   content: `If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining provisions will continue in full force and effect.`,
 },
 {
   id: "entire-agreement",
   title: "18. Entire Agreement",
   content: `These Terms, together with our Privacy Policy and any other legal notices published by us on the Services, shall constitute the entire agreement between you and ${COMPANY_NAME} concerning the Services. These Terms supersede all prior or contemporaneous communications and proposals (whether oral, written, or electronic) between you and ${COMPANY_NAME} with respect to the Services.`,
 },
 {
   id: "waiver",
   title: "19. Waiver",
   content: `No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term, and ${COMPANY_NAME}’s failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.`,
 },
 {
   id: "assignment",
   title: "20. Assignment",
   content: `These Terms, and any rights and licenses granted hereunder, may not be transferred or assigned by you, but may be assigned by ${COMPANY_NAME} without restriction. Any attempted transfer or assignment in violation hereof shall be null and void.`,
 },
 {
   id: "contact",
   title: "21. Contact Information for Legal Concerns",
   content: `If you have any questions about these Terms and Conditions, please contact us at:<br><br><strong>${COMPANY_NAME}</strong><br>${COMPANY_ADDRESS.replace(/, /g, '<br>')}<br>Phone: ${"094608 10765"}<br>Email: <a href="mailto:${LEGAL_EMAIL}">${LEGAL_EMAIL}</a><br>Website: <a href="${WEBSITE_URL}" target="_blank" rel="noopener noreferrer">${WEBSITE_URL} <FiExternalLink style={{verticalAlign: 'middle', marginLeft: '2px'}} size="1em"/></a><br><br>This <strong>Contactless Card Agreement</strong> and <strong>NFC Data Terms and Conditions</strong> are designed to ensure a clear understanding between you and ${COMPANY_NAME}.`,
 },
];

// ----------------------------------------------------------------------
// 3. Theme Context and Provider
// ----------------------------------------------------------------------
const ThemeContext = createContext(undefined);

const CustomThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const storedPreference = localStorage.getItem('nownfc-theme-v3');
    if (storedPreference) return storedPreference;
    return window.matchMedia?.('(prefers-color-scheme: light)')?.matches ? 'dark' : 'light';
  });

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('nownfc-theme-v3', newTheme);
      return newTheme;
    });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.body.className = 'tnc-body-wrapper'; // Apply global body class
    const navbarEl = document.querySelector('.tnc-navbar'); 
    if (navbarEl) {
      const updateNavbarHeight = () => {
        document.documentElement.style.setProperty('--navbar-height', `${navbarEl.offsetHeight}px`);
      };
      updateNavbarHeight();
      
      const resizeObserver = new ResizeObserver(updateNavbarHeight);
      resizeObserver.observe(navbarEl);
      
      window.addEventListener('resize', updateNavbarHeight);
      
      return () => {
        if (navbarEl) { 
          resizeObserver.unobserve(navbarEl);
        }
        window.removeEventListener('resize', updateNavbarHeight);
      };
    }
  }, [theme]);

  const themeValues = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);
  return <ThemeContext.Provider value={themeValues}>{children}</ThemeContext.Provider>;
};

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a CustomThemeProvider');
  }
  return context;
};

// ----------------------------------------------------------------------
// 4. Components
// ----------------------------------------------------------------------

const ThemeToggle = React.memo(() => {
  const { theme, toggleTheme } = useTheme();
  const spring = { type: "spring", stiffness: 500, damping: 30 };
  return (
    <motion.button
      onClick={toggleTheme}
      className="tnc-themeToggle"
      aria-label="Toggle theme"
      whileHover={{ scale: 1.1, rotate: theme === 'light' ? -15 : 15, transition: spring }}
      whileTap={{ scale: 0.9, transition: spring }}
      transition={spring} // For the initial appearance if animated
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={theme} // Ensures remount for new animation
          initial={{ opacity: 0, y: theme === 'light' ? 20 : -20, scale: 0.7, rotate: theme === 'light' ? 90 : -90 }}
          animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
          exit={{ opacity: 0, y: theme === 'light' ? -20 : 20, scale: 0.7, rotate: theme === 'light' ? -90 : 90 }}
          transition={{ ...spring, duration: 0.3 }}
        >
          {theme === 'light' ? <FiMoon /> : <FiSun />}
        </motion.div>
      </AnimatePresence>
    </motion.button>
  );
});

const Navbar = React.memo(() => ( // Removed companyName prop, using constant
  <nav className="tnc-navbar">
    <div className="tnc-navbarLogo">
      <span className="tnc-now">NOW</span> <span className="tnc-nfc-text">NFC</span>
    </div>
    <ThemeToggle />
  </nav>
));

const Footer = React.memo(({ companyName, websiteUrl }) => {
  const currentYear = new Date().getFullYear();
  return (
    <footer className="tnc-footer">
      <p className="tnc-footerText">
        © {currentYear} {companyName}. All rights reserved.
        <br />
        <a href={websiteUrl} className="tnc-footerLink" target="_blank" rel="noopener noreferrer">
          Visit Our Website 
          <motion.span 
            style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '5px' }} 
            className="tnc-footer-arrow"
          >
            <FiArrowRight size="1em"/>
          </motion.span>
        </a>
      </p>
    </footer>
  );
});

const TableOfContents = React.memo(({ sections }) => {
  const [activeId, setActiveId] = useState(sections.length > 0 ? sections[0].id : '');

  useEffect(() => {
    const navbarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height') || '70', 10);
    const observerOptions = {
      rootMargin: `-${navbarHeight + 40}px 0px -${Math.max(250, window.innerHeight * 0.35)}px 0px`,
      threshold: 0.01 
    };
  
    const handleIntersect = entries => {
      const intersectingEntries = entries.filter(entry => entry.isIntersecting);
      if (intersectingEntries.length > 0) {
        intersectingEntries.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActiveId(intersectingEntries[0].target.id);
      }
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    const elements = sections.map(s => document.getElementById(s.id)).filter(el => el);
  
    elements.forEach(el => observer.observe(el));
  
    if (elements.length > 0 && window.scrollY === 0) {
        setActiveId(elements[0].id);
    } else if (elements.length > 0) {
        const currentActiveElement = document.getElementById(activeId);
        if (!currentActiveElement || currentActiveElement.offsetParent === null) {
            const firstVisibleElement = elements.find(el => {
              if (!el || el.offsetParent === null) return false;
              const rect = el.getBoundingClientRect();
              return rect.top < (window.innerHeight - Math.max(250, window.innerHeight * 0.35)) && rect.bottom > (navbarHeight + 40);
            });
            if (firstVisibleElement) setActiveId(firstVisibleElement.id);
            else if (elements[0]) setActiveId(elements[0].id);
        }
    }
    return () => elements.forEach(el => el && observer.unobserve(el));
  }, [sections, activeId]);

  const scrollToSection = useCallback((e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      // The html scroll-padding-top should handle the offset
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  
  const linkVariants = {
    initial: { opacity: 0, x: -25 },
    animate: (i) => ({ 
      opacity: 1, 
      x: 0, 
      transition: { delay: i * 0.04, type: "spring", stiffness: 300, damping: 25 } 
    }),
  };

  return (
    <aside className="tnc-tocSidebar">
      <motion.h3 
        className="tnc-tocTitle"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.1, duration: 0.3 } }}
      >
        Page Contents
      </motion.h3>
      <ul className="tnc-tocList">
        {sections.map((s, index) => (
          <motion.li
            key={s.id}
            className="tnc-tocItem"
            custom={index}
            variants={linkVariants}
            initial="initial"
            animate="animate"
          >
            <a
              href={`#${s.id}`}
              className={`tnc-tocLink ${activeId === s.id ? "tnc-tocLinkActive" : ""}`}
              onClick={e => scrollToSection(e, s.id)}
              aria-current={activeId === s.id ? "page" : undefined}
            >
              {/* Option 1: CSS Border (remove span below) */}
              {/* Option 2: Animated Span (activeTocMarkerSpan CSS handles styling) */}
              <AnimatePresence>
                {activeId === s.id && (
                  <motion.span
                    layoutId="activeTocMarker" 
                    className="tnc-activeTocMarkerSpan" 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 0.2 } }}
                    exit={{ opacity: 0, transition: { duration: 0.15 } }}
                  />
                )}
              </AnimatePresence>
              {s.title.substring(s.title.indexOf('.') + 1).trim()}
            </a>
          </motion.li>
        ))}
      </ul>
    </aside>
  );
});

const AccordionItem = React.memo(({ title, content, id, isOpen, onToggle }) => {
  const headerId = `accordion-header-${id}`;
  const contentId = `accordion-content-${id}`;
  return (
    <div className="tnc-accordionItem" aria-expanded={isOpen}>
      <motion.button 
        className="tnc-accordionHeader" 
        onClick={onToggle} 
        aria-controls={contentId}
        id={headerId}
        whileHover={{ backgroundColor: "var(--border-color)", transition: { duration: 0.15 } }} // Subtle hover
        aria-expanded={isOpen}
      >
        <h3 className="tnc-accordionTitle">{title.substring(title.indexOf('.') + 1).trim()}</h3>
        <motion.span
          className="tnc-accordionIcon"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 350, damping: 25 }}
        >
          <FiChevronDown />
        </motion.span>
      </motion.button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            className="tnc-accordionContentWrapper"
            key={contentId} // Ensure unique key
            initial="collapsed"
            animate="open"
            exit="collapsed"
            role="region"
            aria-labelledby={headerId}
            variants={{
              open: { opacity: 1, height: "auto", y: 0, marginTop: '0.5rem', marginBottom: '0.75rem' },
              collapsed: { opacity: 0, height: 0, y: -10, marginTop: '0rem', marginBottom: '0rem' }
            }}
            transition={{ duration: 0.35, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <div className="tnc-accordionContent tnc-content-html" id={contentId} dangerouslySetInnerHTML={{ __html: content }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const AccordionContainer = React.memo(({ sections }) => {
  const [openAccordion, setOpenAccordion] = useState(null);

  const handleToggle = useCallback((id) => {
    setOpenAccordion(prevOpen => (prevOpen === id ? null : id));
  }, []);
  
  const accordionVariants = {
    initial: { opacity: 0, y: 20 },
    animate: (i) => ({ 
      opacity: 1, 
      y: 0, 
      transition: { delay: i * 0.05, type: "spring", stiffness: 200, damping: 20 } 
    }),
  };

  return (
    <div className="tnc-accordionContainer">
      {sections.map((section, index) => (
        <motion.div
          key={`accordion-item-wrapper-${section.id}`}
          custom={index}
          variants={accordionVariants}
          initial="initial"
          animate="animate"
        >
          <AccordionItem
            id={section.id}
            title={section.title}
            content={section.content}
            isOpen={openAccordion === section.id}
            onToggle={() => handleToggle(section.id)}
          />
        </motion.div>
      ))}
    </div>
  );
});

const AcceptButton = () => {
  const [isAccepted, setIsAccepted] = useState(false);
  useEffect(() => {
    if (localStorage.getItem('nownfc-terms-accepted-v1') === 'true') {
      setIsAccepted(true);
    }
  }, []);

  const handleClick = useCallback(() => {
    setIsAccepted(true);
    localStorage.setItem('nownfc-terms-accepted-v1', 'true');
    // You might want a more subtle notification, e.g., a toast
  }, []);

  if (isAccepted) return null;

  return (
    <AnimatePresence>
        {!isAccepted && (
            <motion.div
                className="tnc-acceptButtonContainer"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.5, ease: "circOut", delay: 0.5 } }}
                exit={{ opacity: 0, y: 60, transition: { duration: 0.3, ease: "circIn" } }}
            >
          
            </motion.div>
        )}
    </AnimatePresence>
  );
};

const getCurrentDateFormatted = () => {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const AnimatedPageTitle = React.memo(({ title }) => {
  const titleChars = Array.from(title);
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: (i = 1) => ({
      opacity: 1,
      transition: { staggerChildren: 0.04, delayChildren: i * 0.06 },
    }),
  };
  const childVariants = {
    visible: {
      opacity: 1, y: 0, scale: 1,
      transition: { type: "spring", damping: 15, stiffness: 200 },
    },
    hidden: { opacity: 0, y: 25, scale: 0.8 },
  };

  return (
    <motion.h1
      className="tnc-pageTitle" // Global h1 style applies
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      aria-label={title}
    >
      {titleChars.map((char, index) => (
        <motion.span key={`${char}-${index}`} variants={childVariants} style={{display: 'inline-block', originX: 0}}>
          {char === " " ? "\u00A0" : char}
        </motion.span>
      ))}
    </motion.h1>
  );
});

const TermsAndConditionsPage = () => {
  const currentDate = useMemo(() => getCurrentDateFormatted(), []);
  const pageTitleMeta = `Terms and Conditions - ${COMPANY_NAME}`;
  const pageDescription = `Review the Terms and Conditions for ${COMPANY_NAME}'s NFC services. Understand our NFC Card Terms, Contactless Card Agreement, Smart Card Usage Policy, and Digital Business Card Terms. Effective ${currentDate}.`;
  const pageKeywords = `NFC Card Terms, Contactless Card Agreement, Smart Card Usage Policy, Digital Business Card Terms, NFC Card Service Terms, NFC Data Terms, ${COMPANY_NAME} User Agreement, Legal, Terms of Service`;

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{pageTitleMeta}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${WEBSITE_URL}/terms-and-conditions`} />
        <meta property="og:title" content={pageTitleMeta} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`${WEBSITE_URL}/terms-and-conditions`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content={COMPANY_NAME} />
        <meta property="article:published_time" content={new Date("2023-12-01").toISOString()} />
        <meta property="article:modified_time" content={new Date().toISOString()} />
        <body className="tnc-body-wrapper" /> {/* Apply global body class */}
      </Helmet>
    
      <motion.div
        className="tnc-pageWrapper"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: "circOut" }} // Smoother page load
      >
        {/* <Navbar /> */}
        <main className="tnc-mainContentContainer">
          <motion.div
            className="tnc-headerSection"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
          >
            <AnimatedPageTitle title="Terms & Conditions" />
            <p className="tnc-pageSubtitle">
              Effective: December 1, 2023 | Last Updated: {currentDate}<br />
              Please read these terms carefully before using {COMPANY_NAME} services.
            </p>
          </motion.div>
          <div className="tnc-termsLayout">
            <motion.div
              className="tnc-tocArea"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
            >
              <TableOfContents sections={termsSectionsData} />
            </motion.div>
            <div className="tnc-termsContentArea">
              <AccordionContainer sections={termsSectionsData} />
              <div className="tnc-desktopContentWrapper">
                {termsSectionsData.map((section, index) => (
                  <motion.section
                    key={`desktop-content-${section.id}`}
                    id={section.id}
                    className="tnc-desktopSectionItem"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.1 }} 
                    transition={{ duration: 0.6, delay: index * 0.05, ease: "easeOut" }} 
                  >
                    <h2>{section.title}</h2>
                    <div className="tnc-content-html" dangerouslySetInnerHTML={{ __html: section.content }} />
                  </motion.section>
                ))}
              </div>
            </div>
          </div>
        </main>
        <AcceptButton />
        <Footer companyName={COMPANY_NAME} websiteUrl={WEBSITE_URL} />
      </motion.div>
    </>
  );
};

// ----------------------------------------------------------------------
// 5. Main App Component
// ----------------------------------------------------------------------
function App() {
  return (
    <HelmetProvider>
      <CustomThemeProvider>
        <TermsAndConditionsPage />
      </CustomThemeProvider>
    </HelmetProvider>
  );
}

export default App;